import { Flex } from "antd"
import type { ItemType } from "antd/es/menu/interface"
import React from "react"

import { RowActionsMenu } from "../../../components/EntityTable/RowActionsMenu.tsx"
import { INVOICE_ORIGINS, INVOICE_STATUSES } from "../types/invoices.types.ts"

export type InvoiceRowAction =
  | "edit"
  | "issue"
  | "delete"
  | "preview"
  | "download"
  | "send"
  | "void"
  | "delivery_history"
  | "change_status"

type Translate = (key: string, options?: Record<string, unknown>) => string

interface InvoiceActionRecord {
  status?: number
  origin?: number
  is_generated?: boolean
  invoice_number?: string | null
  lines?: unknown[]
  attachments?: unknown[]
}

export interface InvoiceRowActionFlags {
  editAllowed?: boolean
  deleteAllowed?: boolean
  voidAllowed?: boolean
  changeStatusAllowed?: boolean
  deliveryHistoryEnabled?: boolean
}

interface BuildArgs extends InvoiceRowActionFlags {
  record: InvoiceActionRecord
  t: Translate
}

export const getInvoiceMenuItems = ({
  record,
  t,
  editAllowed,
  deleteAllowed = editAllowed,
  voidAllowed,
  changeStatusAllowed = voidAllowed,
  deliveryHistoryEnabled = true
}: BuildArgs): ItemType[] => {
  const items: ItemType[] = []
  const isGenerated =
    record.origin === INVOICE_ORIGINS.GENERATED || Boolean(record.is_generated)
  const isDraft = record.status === INVOICE_STATUSES.DRAFT
  const isVoid = record.status === INVOICE_STATUSES.VOID
  const canDownload = isGenerated || (record.attachments?.length ?? 0) > 0
  // Broader than the download: a generated invoice only has a document once it is numbered,
  // while an external one is previewable from its attachment.
  const hasDocument =
    (isGenerated && Boolean(record.invoice_number)) ||
    (record.attachments?.length ?? 0) > 0

  if (isGenerated && isDraft) {
    if (editAllowed) {
      items.push({ key: "edit", label: t("invoices.actions.edit") })
      if (record.lines?.length) {
        items.push({ key: "issue", label: t("invoices.actions.issue") })
      }
    }
    if (deleteAllowed) {
      items.push({
        key: "delete",
        label: t("invoices.actions.delete"),
        danger: true
      })
    }
    return items
  }

  if (!isDraft) {
    if (hasDocument) {
      items.push({ key: "preview", label: t("invoices.actions.preview") })
    }
    if (canDownload) {
      items.push({ key: "download", label: t("invoices.actions.download") })
    }
    if (isGenerated && deliveryHistoryEnabled) {
      items.push({
        key: "delivery_history",
        label: t("invoices.actions.deliveryHistory")
      })
    }
    if (!isVoid) {
      if (editAllowed) {
        items.push({ key: "send", label: t("invoices.actions.send") })
      }
      if (changeStatusAllowed) {
        items.push({
          key: "change_status",
          label: t("invoices.actions.changeStatus")
        })
      }
      if (voidAllowed) {
        items.push({
          key: "void",
          label: t("invoices.actions.void"),
          danger: true
        })
      }
    }
  }

  return items
}

interface InvoiceRowActionsProps extends InvoiceRowActionFlags {
  record: InvoiceActionRecord
  t: Translate
  disabled?: boolean
  onSelectRow: (action: InvoiceRowAction) => void
}

export const InvoiceRowActions = ({
  record,
  t,
  disabled,
  onSelectRow,
  ...flags
}: InvoiceRowActionsProps) => {
  const items = getInvoiceMenuItems({ record, t, ...flags })
  if (!items.length) return null

  return (
    <Flex justify="center" className="w-20 mx-auto">
      <RowActionsMenu
        items={items}
        disabled={disabled}
        onClick={({ key }) => onSelectRow(key as InvoiceRowAction)}
      />
    </Flex>
  )
}
